import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Boxes, Package, RefreshCw, BarChart3, ShieldCheck, Users } from "lucide-react";
import { AdminOrders } from "./AdminOrders";
import { AdminSubscriptions } from "./AdminSubscriptions";
import { AdminAffiliates } from "./AdminAffiliates";
import { AdminAnalytics } from "./AdminAnalytics";
import { AdminSecurity } from "./AdminSecurity";

const EMPTY = {
  name: "", slug: "", price: "", compare_at_price: "", short_description: "",
  description: "", category: "Peptide Fragments", badge: "", image: "", format: "",
  servings: "", benefits: "", ingredients: "", purity: "", coa: "", featured: false, in_stock: true,
};

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "orders", label: "Orders", icon: Package },
  { key: "subscriptions", label: "Subscriptions", icon: RefreshCw },
  { key: "affiliates", label: "Affiliates", icon: Users },
  { key: "products", label: "Products", icon: Boxes },
  { key: "security", label: "Security", icon: ShieldCheck },
];

export default function Admin() {
  const [tab, setTab] = useState("analytics");
  const [authed, setAuthed] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(() => { setAuthed(true); load(); }).catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem("cl_admin_token");
    navigate("/admin/login");
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...p,
      compare_at_price: p.compare_at_price ?? "",
      badge: p.badge ?? "",
      purity: p.purity ?? "",
      coa: p.coa ?? "",
      benefits: (p.benefits || []).join("\n"),
    });
    setShowForm(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      slug: form.slug || slugify(form.name),
      price: parseFloat(form.price) || 0,
      compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
      short_description: form.short_description,
      description: form.description,
      category: form.category || "Peptide Fragments",
      badge: form.badge || null,
      image: form.image,
      images: form.image ? [form.image] : [],
      format: form.format,
      servings: form.servings,
      benefits: form.benefits.split("\n").map((b) => b.trim()).filter(Boolean),
      ingredients: form.ingredients,
      purity: form.purity || null,
      coa: form.coa || null,
      featured: !!form.featured,
      in_stock: !!form.in_stock,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!authed) {
    return <div className="min-h-screen bg-[#070B1C] flex items-center justify-center text-sm tracking-widest uppercase text-white/60">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#070B1C] text-white">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/15">
          <div>
            <div className="font-display font-bold text-2xl tracking-tighter uppercase">Admin</div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-electric mt-1">Century Labs Console</p>
          </div>
          <div className="flex gap-3">
            {tab === "products" && (
              <button onClick={openNew} className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold" data-testid="admin-add-product">
                <Plus size={16} /> Add Product
              </button>
            )}
            <button onClick={logout} className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold" data-testid="admin-logout">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold border transition-colors duration-200 sq-md sq-press ${
                  active ? "bg-electric text-black border-electric" : "bg-transparent text-white border-white/25 hover:border-electric hover:text-electric"
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "analytics" && <AdminAnalytics />}
        {tab === "orders" && <AdminOrders />}
        {tab === "subscriptions" && <AdminSubscriptions />}
        {tab === "affiliates" && <AdminAffiliates />}
        {tab === "security" && <AdminSecurity />}

        {tab === "products" && (
          loading ? (
            <p className="py-20 text-center text-sm tracking-widest uppercase text-white/60">Loading...</p>
          ) : (
            <div className="border border-white/15 sq-lg overflow-hidden" data-testid="admin-product-table">
              <div className="hidden md:grid grid-cols-[80px_1fr_140px_120px_120px] border-b border-white/15 bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold">
                <div className="p-3">Image</div>
                <div className="p-3">Name</div>
                <div className="p-3">Category</div>
                <div className="p-3">Price</div>
                <div className="p-3 text-right">Actions</div>
              </div>
              {products.map((p) => (
                <div key={p.id} className="grid grid-cols-2 md:grid-cols-[80px_1fr_140px_120px_120px] items-center border-b border-white/12 last:border-b-0" data-testid={`admin-row-${p.slug}`}>
                  <div className="p-3">
                    <div className="w-12 h-12 border border-white/15 bg-[#121A3A] overflow-hidden sq-sm">
                      {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold text-sm uppercase text-white">{p.name}</div>
                    <div className="text-[10px] text-white/40">{p.slug}{p.featured ? " · featured" : ""}</div>
                  </div>
                  <div className="p-3 text-xs uppercase tracking-wider text-white/70">{p.category}</div>
                  <div className="p-3 font-display font-bold text-white">{money(p.price)}</div>
                  <div className="p-3 flex gap-2 md:justify-end">
                    <button onClick={() => openEdit(p)} className="p-2 border border-white/20 text-white hover:bg-electric hover:text-black hover:border-electric transition-colors duration-200 sq-sm sq-press" data-testid={`admin-edit-${p.slug}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 border border-white/20 text-white hover:bg-electric hover:text-black hover:border-electric transition-colors duration-200 sq-sm sq-press" data-testid={`admin-delete-${p.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Product form modal */}
        {showForm && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4">
            <div className="bg-[#0B1024] border border-white/15 w-full max-w-2xl my-auto sq-xl" data-testid="admin-product-form">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/15 sticky top-0 bg-[#0B1024]">
                <h2 className="font-display font-bold text-lg tracking-tighter uppercase text-white">
                  {editing ? "Edit Product" : "New Product"}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-white/70 hover:text-electric" data-testid="admin-form-close"><X size={20} /></button>
              </div>
              <form onSubmit={save} className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="cl-input" data-testid="field-name" /></Field>
                  <Field label="Slug (optional)"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from name" className="cl-input" data-testid="field-slug" /></Field>
                  <Field label="Price ($)"><input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="cl-input" data-testid="field-price" /></Field>
                  <Field label="Compare-at Price ($)"><input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => set("compare_at_price", e.target.value)} className="cl-input" data-testid="field-compare" /></Field>
                  <Field label="Category"><input value={form.category} onChange={(e) => set("category", e.target.value)} className="cl-input" data-testid="field-category" /></Field>
                  <Field label="Badge (optional)"><input value={form.badge} onChange={(e) => set("badge", e.target.value)} placeholder="Bestseller / New" className="cl-input" data-testid="field-badge" /></Field>
                  <Field label="Purity (optional)"><input value={form.purity} onChange={(e) => set("purity", e.target.value)} placeholder="99.7%" className="cl-input" data-testid="field-purity" /></Field>
                  <Field label="COA (optional)"><input value={form.coa} onChange={(e) => set("coa", e.target.value)} placeholder="COA on file" className="cl-input" data-testid="field-coa" /></Field>
                  <Field label="Format"><input value={form.format} onChange={(e) => set("format", e.target.value)} className="cl-input" data-testid="field-format" /></Field>
                  <Field label="Servings"><input value={form.servings} onChange={(e) => set("servings", e.target.value)} className="cl-input" data-testid="field-servings" /></Field>
                </div>
                <Field label="Image URL"><input value={form.image} onChange={(e) => set("image", e.target.value)} className="cl-input" data-testid="field-image" /></Field>
                <Field label="Short Description"><input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="cl-input" data-testid="field-short" /></Field>
                <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="cl-input resize-none" data-testid="field-description" /></Field>
                <Field label="Benefits (one per line)"><textarea rows={3} value={form.benefits} onChange={(e) => set("benefits", e.target.value)} className="cl-input resize-none" data-testid="field-benefits" /></Field>
                <Field label="Ingredients"><textarea rows={2} value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="cl-input resize-none" data-testid="field-ingredients" /></Field>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold text-white">
                    <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="field-featured" /> Featured
                  </label>
                  <label className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold text-white">
                    <input type="checkbox" checked={form.in_stock} onChange={(e) => set("in_stock", e.target.checked)} data-testid="field-instock" /> In Stock
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving} className="btn-primary flex-1 py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50" data-testid="admin-form-save">
                    {saving ? "Saving..." : editing ? "Update Product" : "Create Product"}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="btn-ghost px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">{label}</label>
    {children}
  </div>
);
